import { useCallback, useContext, useState } from 'react';
import { ITrackSubmitProps } from './types';
import { formContextObj } from '../../containers/FormContainer/context';
import { useDeviceType } from '@cian/ui-kit/responsive/hooks/useDeviceType';
import {
  trackAddressFailEvent,
  trackBackToPageEvent,
  trackClickEvent,
  trackCompleteButtonEvent,
  trackFormSend,
  trackPrevStepEvent,
} from './tracking';

export const useFormTracking = () => {
  const deviceType = useDeviceType();
  const { currentQuestion, answersState } = useContext(formContextObj);

  const [isDirty, setDirty] = useState(false);
  const trackChange = useCallback(() => {
    setDirty(true);
  }, []);

  const trackSubmit = useCallback(
    ({ field, isRequired, value }: ITrackSubmitProps) => {
      trackClickEvent({
        field,
        answersState,
        deviceType,
        isRequired,
        value,
        isDirty,
      });
    },
    [isDirty, answersState, deviceType],
  );

  const trackLteWithoutGte = useCallback(
    ({ field, isRequired }: ITrackSubmitProps) => {
      if (isDirty) {
        return;
      }
      trackClickEvent({
        field: `${field}_lte`,
        answersState,
        deviceType,
        isRequired,
        value: null,
        isDirty: true,
      });
    },
    [isDirty, answersState, deviceType],
  );

  const trackBackToPage = useCallback(() => {
    trackBackToPageEvent({ field: currentQuestion.answerField, answersState, deviceType });
  }, [currentQuestion, answersState, deviceType]);

  const trackPrevStep = useCallback(
    (currentStepField: string) => {
      trackPrevStepEvent({
        field: currentStepField,
        answersState,
        deviceType,
      });
    },
    [answersState, deviceType],
  );

  const trackAddressFail = useCallback(() => {
    trackAddressFailEvent({ field: currentQuestion.answerField, answersState, deviceType });
  }, [currentQuestion, answersState, deviceType]);

  const trackCompleteButton = useCallback(() => {
    trackCompleteButtonEvent({ field: currentQuestion.answerField, answersState, deviceType });
  }, [currentQuestion, answersState, deviceType]);

  const trackFormSendEvent = useCallback(() => {
    trackFormSend({ field: currentQuestion.answerField, answersState, deviceType });
  }, [currentQuestion, answersState, deviceType]);

  return {
    trackChange,
    trackSubmit,
    trackBackToPage,
    trackPrevStep,
    trackAddressFail,
    trackCompleteButton,
    trackLteWithoutGte,
    trackFormSendEvent,
  };
};
